import {useGetRecentData} from "api/api-hooks/datasets-hooks";
import {DatasetDataInfoResponse} from "api/model/dataset/DatasetDataInfoResponse";
import CalendarWrapper from "components/basic/calendar-wrapper/CalendarWrapper";
import {clipboardIcon} from "components/dashboard/charts/icons";
import {PlotDataEx} from "components/dashboard/charts/interfaces";
import {
    minimalNumber,
    timeSetWithConfig,
    validatedConfigIsCorrect
} from "components/dashboard/charts/sankey-chart/sankey-utils";
import {FormalSankeyConfig, SankeyDiagramProps} from "components/dashboard/charts/sankey-chart/sankey-types";
import {
    transformDataToSankeyData,
} from "components/dashboard/charts/sankey-chart/TransformData";
import {copyToClipBoard} from "components/dashboard/dataset-info-by-id/utils";

import useDashboardEditMode from "hooks/useDashboardEditMode";
import {Button} from "primereact/button";
import {ProgressSpinner} from "primereact/progressspinner";
import {Toast} from "primereact/toast";
import React, {useEffect, useRef, useState} from "react";
import Plot from "react-plotly.js";
import {useResizeDetector} from "react-resize-detector";

const Sankey = (props: SankeyDiagramProps) => {
    const plotRef = useRef(null);
    const toast = useRef<Toast | null>(null);
    const {width, ref} = useResizeDetector({handleHeight: false, refreshOptions: {trailing: true}});


    const [date, setDate] = useState<Date>(timeSetWithConfig(props.referenceTime, {
        alignStartTo: props.alignStartTo,
        timeOffset: props.timeOffset,
        timeOffsetUnits: props.timeOffsetUnits
    }));

    const [sankeyConfig, setSankeyConfig] = useState<FormalSankeyConfig>();
    const [sankeyData, setSankeyData] = useState<Partial<PlotDataEx>>();

    const editMode = useDashboardEditMode();

    const [error, setError] = useState<string>("");

    const {
        data,
        isLoading,
        error: responseError,
        refetch
    } = useGetRecentData(props.graph.links.map(e => e.identifier),
        {
            refTime: date.toISOString(),
            usePromiseHandler: (data) => {
                const d = data.data as DatasetDataInfoResponse[];
                if (d) {
                    return d.map(e => {
                        if (e.data.length > 0 && e.data[0].value <= 0) {
                            return {...e, data: [{...e.data[0], value: minimalNumber}]};
                        }

                        return e;
                    });
                }

                return data.data;
            }
        });

    useEffect(() => {
        if (!isLoading && data) {
            const hasEmptyData = (data as DatasetDataInfoResponse[]).some(e => e.data.length === 0);

            if (hasEmptyData) {
                const errorMessages = (data as DatasetDataInfoResponse[])
                    .filter(e => e.data.length === 0)
                    .map(e => `No data in dataset: ${e.identifier}`);

                setError(errorMessages.join(", \n"));
                return;
            }

            if (validatedConfigIsCorrect(props.graph) !== "") {
                setError(`No node in config called - ${validatedConfigIsCorrect(props.graph)}`);
                return;
            }


            setError("");
            setSankeyConfig(transformDataToSankeyData(props.graph, data, props.decimalPlaces, props.percentageDecimalPlaces));

        }
    }, [data, isLoading]);


    useEffect(() => {
        if (sankeyConfig) {
            setSankeyData({
                type: "sankey",
                domain: {
                    x: [0, 1],
                    y: [0, 1]
                },
                node: {
                    label: sankeyConfig?.nodeNames,
                    x: props.nodesPosition ? props.nodesPosition.x : undefined,
                    y: props.nodesPosition ? props.nodesPosition.y : undefined,
                    hoverinfo: "none",
                    thickness: 30,
                    pad: props.nodesGap || 20,
                },
                link: {
                    source: sankeyConfig?.links.source,
                    target: sankeyConfig?.links.target,
                    value: sankeyConfig?.links.value,
                    label: sankeyConfig?.links.linkName,
                    hovertemplate: `%{label}<br>%{customdata}<extra></extra>`,
                    customdata: sankeyConfig?.links.customdata
                },
                arrangement: editMode ? "freeform" : "fixed"
            } as Partial<PlotDataEx>);
        }
    }, [sankeyConfig]);

    const copyNodesPosition = () => {
        // @ts-ignore
        const plotProps = plotRef.current?.props;
        const data = plotProps?.data;

        if (data && data.length > 0 && data[0].node) {
            const nodesConf = data[0].node;

            if (nodesConf.x && nodesConf.y) {
                const tempProps = {...props};
                tempProps.nodesPosition = {x: nodesConf.x, y: nodesConf.y};
                const jsonString = JSON.stringify(tempProps, null, 2);

                copyToClipBoard(jsonString)
                    .then(() => {
                        toast.current?.show({severity: "success", summary: "Success", detail: "Changed config copied"});
                    })
                    .catch((error) => {
                        toast.current?.show({severity: "error", summary: "Error", detail: error});
                        console.error(error);
                    });
            } else {
                toast.current?.show({severity: "warn", summary: "Warning", detail: "Nodes haven't changed position"});
                console.warn("No change in nodes position");
            }
        }
    };


    const DifferReturn = () => {
        if (error) {
            return <pre>{error}</pre>;
        } else if (responseError) {
            return <>Error : {responseError.message}</>;
        } else if (isLoading) {
            return <div className={"flex justify-content-center"}><ProgressSpinner style={{overflow: "hidden"}}/></div>;
        } else if (!sankeyConfig) {
            return <>Wrong Config</>;
        } else if (!sankeyData) {
            return <>No data</>;
        }

        return <Plot ref={plotRef}
                     data={[sankeyData]}
                     config={{
                         displaylogo: false,
                         modeBarButtonsToAdd: editMode ? [
                             {
                                 title: "Copy Nodes Position",
                                 name: "Copy Nodes Position",
                                 icon: clipboardIcon,
                                 click: copyNodesPosition
                             }
                         ] : []
                     }}
                     layout={{
                         width: width,
                         height: props.height || 400,
                         margin: {
                             l: 0,
                             t: 0,
                             r: 0,
                             b: 20
                         }
                     }}
        />;
    };

    return <div ref={ref} className={"flex flex-column gap-2"}>
        <Toast ref={toast}/>
        <div className={"flex gap-2 non-printable"}>
            <CalendarWrapper value={date} onChange={setDate}/>
            <Button icon={"pi pi-refresh"} className={"p-button-sm"} onClick={() => refetch()}/>
        </div>
        <DifferReturn/>
    </div>;
};

export default Sankey;